import type { ReactNode } from 'react';

export function ChartCard({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="rounded-xl border border-slate-700 bg-slate-800/60 p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-sm font-medium text-slate-200">{title}</h2>
        {action}
      </div>
      {children}
    </div>
  );
}

export const AXIS = { fill: '#94a3b8', fontSize: 11 } as const;
export const GRID = '#1e293b';
export const TOOLTIP_STYLE = {
  background: '#0f172a',
  border: '1px solid #334155',
  borderRadius: 8,
} as const;
export const PALETTE = [
  '#3b82f6',
  '#22c55e',
  '#f59e0b',
  '#a855f7',
  '#ef4444',
  '#14b8a6',
  '#eab308',
];

/** Color de la superficie de la tarjeta: separa segmentos apilados y marcas. */
export const SURFACE = '#1e293b';

/**
 * Serie categórica para las cuentas corporativas, escalonada para ESTA
 * superficie oscura y validada como conjunto (OKLab ×100, pares adyacentes —
 * que es lo que tocan las barras apiladas y la leyenda):
 *
 *   banda de luminosidad L 0.48–0.67 ✓ · croma ≥ 0.1 ✓ · contraste ≥ 3:1 ✓
 *   separación CVD peor par 8.4 (protan) / 8.7 (tritan) ✓ · visión normal 19.3 ✓
 *
 * El color sigue a la CUENTA, nunca a su puesto en el ranking: filtrar sedes
 * cambia el orden, y si el color siguiera al puesto se repintaría todo. A partir
 * del séptimo nombre no se inventa un tono: se pliega en «Otras» (gris).
 */
export const CUENTAS_SERIES = ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#9085e9'];
export const OTRAS_COLOR = '#64748b';
export const MAX_CUENTAS_SERIES = CUENTAS_SERIES.length;
export const OTRAS_LABEL = 'Otras';

/**
 * Color por CANAL de venta. A diferencia de las cuentas, el catálogo es fijo y
 * cerrado (5 entradas), así que el color se fija por entidad de una vez: filtrar
 * sedes o meses no repinta nada.
 *
 * Los cinco tonos salen de `CUENTAS_SERIES` reordenados para que los pares
 * ADYACENTES en la pila (CDR→VIP→VTA→FRO→EXP→Otros) sean los que más se
 * separan. Validado como conjunto contra ESTA superficie (OKLab ×100, pares
 * adyacentes, que es lo que tocan las barras apiladas y la leyenda):
 *
 *   banda de luminosidad L 0.48–0.67 ✓ · croma ≥ 0.1 ✓ · contraste ≥ 3:1 ✓
 *   separación CVD peor par 8.4 (protan) · visión normal 19.8 ✓
 *
 * «Otros» va en gris a propósito: es el residuo de lo que el mapa de canales no
 * reconoce, no una categoría más, y leerlo como tal es justo lo que se busca.
 */
export const CANAL_COLORS: Record<string, string> = {
  CDR: '#3987e5',
  VIP: '#c98500',
  VTA: '#199e70',
  FRO: '#d95926',
  EXP: '#9085e9',
  OTROS: OTRAS_COLOR,
};

/**
 * Color estable por cuenta: el orden lo fija el ranking del período completo
 * (no el mes visible) y se reparte en el orden del catálogo, sin ciclar.
 */
export function coloresPorCuenta(cuentasOrdenadas: readonly string[]): Map<string, string> {
  const out = new Map<string, string>();
  cuentasOrdenadas.slice(0, MAX_CUENTAS_SERIES).forEach((c, i) => out.set(c, CUENTAS_SERIES[i]!));
  return out;
}
